import { texteEnLangue } from '@artosera/shared';
import { Button, Input, Textarea } from '@artosera/ui';
import { formatDate } from '@artosera/shared';
import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estApiError } from '../../../api/errors.js';
import { useActualitesDeLExposition, useCreerActualite } from '../../../api/actualites.js';
import { Field } from '../../Field.js';
import styles from './Tabs.module.css';

export interface ActualitesTabProps {
  galerieSlug: string;
  expositionId: string;
  peutEcrire: boolean;
}

/**
 * Actualités (S3-d, périmètre volontairement léger) : la liste des
 * actualités déjà rattachées à cette exposition (`GET …/actualites
 * ?expositionId=`, § 15) et une création rapide. Un module « actualités »
 * complet (édition, archivage, médias) reste hors périmètre de cet écran.
 */
export function ActualitesTab({
  galerieSlug,
  expositionId,
  peutEcrire,
}: ActualitesTabProps): JSX.Element {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const langue = i18n.language === 'en' ? 'en' : 'fr';

  const actualites = useActualitesDeLExposition(galerieSlug, expositionId);
  const creer = useCreerActualite(galerieSlug);

  const [ouvert, setOuvert] = useState(false);
  const [titre, setTitre] = useState('');
  const [texte, setTexte] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);

  async function soumettre(): Promise<void> {
    setErreur(null);
    try {
      await creer.mutateAsync({
        titre: { fr: titre },
        texte: { fr: texte },
        expositions: [expositionId],
      });
      setOuvert(false);
      setTitre('');
      setTexte('');
    } catch (error) {
      setErreur(estApiError(error) ? error.message : t('commun:erreurReseau'));
    }
  }

  return (
    <div className={styles.formulaire}>
      {(actualites.data ?? []).length === 0 ? (
        <p className={styles.vide}>{t('regie:expositions.actualites.aucuneActualite')}</p>
      ) : (
        <table className={styles.tableau}>
          <thead>
            <tr>
              <th scope="col">{t('regie:expositions.actualites.titreChamp')}</th>
              <th scope="col">{t('regie:expositions.actualites.dateChamp')}</th>
            </tr>
          </thead>
          <tbody>
            {(actualites.data ?? []).map((actualite) => (
              <tr key={actualite.id}>
                <td>{texteEnLangue(actualite.titre, langue)}</td>
                <td>{formatDate(new Date(actualite.date), langue, 'court')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {peutEcrire ? (
        ouvert ? (
          <div className={styles.section}>
            <Field label={t('regie:expositions.actualites.titreChamp')} htmlFor="actualite-titre">
              <Input
                id="actualite-titre"
                value={titre}
                onChange={(event) => setTitre(event.target.value)}
              />
            </Field>
            <Field label={t('regie:expositions.actualites.texteChamp')} htmlFor="actualite-texte">
              <Textarea
                id="actualite-texte"
                rows={4}
                value={texte}
                onChange={(event) => setTexte(event.target.value)}
              />
            </Field>
            {erreur ? (
              <p className={styles.erreur} role="alert">
                {erreur}
              </p>
            ) : null}
            <div>
              <Button
                onClick={() => void soumettre()}
                loading={creer.isPending}
                disabled={titre.trim().length === 0 || texte.trim().length === 0}
              >
                {creer.isPending
                  ? t('regie:expositions.actualites.creation')
                  : t('regie:expositions.actualites.creer')}
              </Button>
            </div>
          </div>
        ) : (
          <Button variant="secondary" size="sm" onClick={() => setOuvert(true)}>
            {t('regie:expositions.actualites.nouvelle')}
          </Button>
        )
      ) : null}
    </div>
  );
}
